import { DraggableProvided, DraggableStateSnapshot } from '@hello-pangea/dnd'
import { styled, Typography } from '@mui/material'
import Icon from '@mui/material/Icon'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import React from 'react'

import CustomTooltip from '@/app/component/atom/tooltip'
import IconText from '@/app/component/layout/icon-text'
import { LocalAction, LocalActionType, LocalNonEmptyAction } from '@/app/module/campaigns/types'
import { hasPreview } from '@/app/module/campaigns/utils/actions/helpers'
import { ActionOption } from '@/app/module/campaigns/utils/actions/options'
import { FilesType, PersonalizationType } from '@/app/types'

import ActionPreviewContainer from './action-preview-container'
import AddToLibraryButton, { editableActions } from './add-to-library-button'

type Props = {
  action: LocalAction
  disabled?: boolean
  dragProvided: DraggableProvided
  dragSnapshot: DraggableStateSnapshot
  errorMessage?: string
  files: FilesType
  index: number
  isLast: boolean
  personalizationList: PersonalizationType
  values: ActionOption[]

  onChange: (value: { value: LocalActionType }) => void
  onCopy: (action: LocalNonEmptyAction) => Promise<void | Error>
  onMove: (from: number, to: number) => void
  onRemove: () => void
}

const Action: React.FC<Props> = ({
  action,
  disabled,
  dragProvided,
  dragSnapshot,
  errorMessage,
  files,
  isLast,
  personalizationList,
  values,
  onChange,
  onCopy,
  onRemove,
}) => {
  let dragCursor = 'grab'
  if (disabled) {
    dragCursor = 'not-allowed'
  } else if (dragSnapshot.isDragging) {
    dragCursor = 'grabbing'
  }

  const selectedOption = values.find((v) => v.value === action.type)
  const label = selectedOption?.label || action.type
  const icon = selectedOption?.icon
  const hasActionPreview = hasPreview(action)
  const hasError = Boolean(errorMessage)
  const isSingleBlockPreview = action.type === LocalActionType.RunQuota

  const iconColor = disabled ? 'disabled' : 'action'

  return (
    <div
      {...dragProvided.draggableProps}
      style={{
        ...dragProvided.draggableProps.style,
        marginBottom: 12,
      }}
      ref={dragProvided.innerRef}
    >
      <Container className="campaign-action-item" data-testid="campaign-action-item">
        <div
          style={{
            cursor: dragCursor,
            borderRadius: '0',
            height: '100%',
            alignSelf: 'center',
            maxHeight: 'none',
            marginRight: 6,
          }}
          {...dragProvided.dragHandleProps}
        >
          <Icon color={iconColor}>drag_indicator</Icon>
        </div>
        <MiddleSection>
          {!isSingleBlockPreview && (
            <InfoContainer hasError={hasError}>
              <IconText>
                {typeof icon === 'string' ? <Icon color={iconColor}>{icon}</Icon> : icon?.()}
                <Typography
                  className="selected-action-label"
                  color={disabled ? 'textDisabled' : 'textSecondary'}
                  variant="subtitle1"
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {label}
                </Typography>
              </IconText>
            </InfoContainer>
          )}
          {hasActionPreview && (
            <InfoContainer hasError={hasError}>
              <ActionPreviewContainer
                action={action}
                disabled={disabled}
                files={files}
                personalizationList={personalizationList}
                onEdit={() => onChange({ value: action.type as LocalActionType })}
              />
            </InfoContainer>
          )}
          {hasError && (
            <ErrorContainer>
              <CustomTooltip title={errorMessage}>
                <Icon color="error" fontSize="small">
                  error_outline
                </Icon>
              </CustomTooltip>
            </ErrorContainer>
          )}
        </MiddleSection>
        <InputAdornment position="end" style={{ height: '38px', marginBottom: '0.5rem', maxHeight: 'none' }}>
          <IconButton
            disabled={editableActions.indexOf(action.type as string) === -1 || disabled}
            onClick={() => onChange({ value: action.type as LocalActionType })}
          >
            <Icon>create</Icon>
          </IconButton>
          <IconButton disabled={isLast} onClick={onRemove}>
            <Icon>delete</Icon>
          </IconButton>
          {!!action.type && <AddToLibraryButton action={action} onCopy={onCopy} />}
        </InputAdornment>
      </Container>
    </div>
  )
}

const Container = styled('div')({
  display: 'flex',
  alignItems: 'flex-end',
  maxWidth: 1080,
})

const MiddleSection = styled('div')({
  display: 'flex',
  alignItems: 'flex-end',
  flex: 1,
  position: 'relative',
})

const InfoContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'hasError',
})<{ hasError: boolean }>(({ hasError, theme }) => ({
  borderBottom: hasError ? `1px dotted ${theme.palette.error.main}` : '1px dotted rgba(0, 0, 0, 0.54)',
  flex: 1,
  overflow: 'hidden',
  paddingBottom: '4px',
  marginBottom: '8px',
  marginRight: '8px',
}))

const ErrorContainer = styled('div')(({ theme }) => ({
  alignItems: 'center',
  color: theme.palette.error.main,
  display: 'flex',
  gap: 6,
  justifyContent: 'flex-end',
  marginLeft: 'auto',
  marginBottom: 16,
}))

export default Action
